import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Bell, CheckCheck } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { fetchNotifications } from '../lib/notifications'
import styles from './NotificationBell.module.css'

// Unread-count bell with a popover of the most recent notifications, plus the
// original /notifications path for the full list. Refreshes on an interval and
// follows Supabase Realtime changes when a channel is available.
export default function NotificationBell() {
  const [unread, setUnread] = useState(0)
  const [items, setItems] = useState([])
  const [open, setOpen] = useState(false)
  const wrapRef = useRef(null)

  const refresh = useCallback(async () => {
    try {
      const { notifications, unread: count } = await fetchNotifications({ limit: 100 })
      setUnread(count)
      // The popover reuses the exact payload the badge already fetches — no
      // extra request, and an item list for free.
      setItems((notifications || []).slice(0, 8))
    } catch {
      /* ignore transient errors */
    }
  }, [])

  const subscribe = useCallback(() => {
    if (!supabase) return () => {}
    const holder = { current: () => {} }
    supabase.auth.getUser().then(({ data }) => {
      const uid = data?.user?.id
      if (!uid) return
      const channel = supabase
        .channel('notification-bell')
        .on(
          'postgres_changes',
          { event: 'INSERT', schema: 'public', table: 'notifications', filter: `user_id=eq.${uid}` },
          () => refresh()
        )
        .subscribe()
      holder.current = () => supabase.removeChannel(channel)
    })
    return () => holder.current()
  }, [refresh])

  useEffect(() => {
    refresh()
    const id = setInterval(refresh, 60000)
    const cleanup = subscribe()
    return () => {
      clearInterval(id)
      cleanup()
    }
  }, [refresh, subscribe])

  useEffect(() => {
    if (!open) return undefined
    const onPointerDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false)
    }
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const label = (n) => n.title || n.message || n.body || n.type || 'Notification'

  return (
    <div className={styles.root} ref={wrapRef}>
      <button
        type="button"
        className={styles.trigger}
        aria-label={`Notifications${unread ? ` (${unread} unread)` : ''}`}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <Bell size={18} />
        {unread > 0 && (
          <span className={styles.badge} aria-hidden="true">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className={styles.popover} role="dialog" aria-label="Notifications">
          <div className={styles.popoverHead}>
            <span className={styles.popoverTitle}>Notifications</span>
            <span className={styles.popoverCount}>
              {unread > 0 ? `${unread} unread` : 'All caught up'}
            </span>
          </div>

          {items.length === 0 ? (
            <p className={styles.empty}>Nothing new right now. New activity will show up here.</p>
          ) : (
            <div className={styles.list}>
              {items.map((n) => (
                <Link
                  key={n.id}
                  to="/notifications"
                  className={`${styles.item} ${n.read ? '' : styles['item--unread']}`}
                  onClick={() => setOpen(false)}
                >
                  <span className={`${styles.dot} ${n.read ? '' : styles['dot--on']}`} aria-hidden="true" />
                  <span className={styles.itemBody}>
                    <span className={styles.itemTitle}>{label(n)}</span>
                    <span className={styles.itemMeta}>
                      {n.read ? 'Read' : 'Unread'}
                      {n.created_at ? ` · ${new Date(n.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}` : ''}
                    </span>
                  </span>
                </Link>
              ))}
            </div>
          )}

          <div className={styles.foot}>
            <Link to="/notifications" className={styles.footLink} onClick={() => setOpen(false)}>
              <CheckCheck size={14} /> View all
            </Link>
          </div>
        </div>
      )}
    </div>
  )
}
